// Early dev-only suppression of benign ResizeObserver overlay noise
if (process.env.NODE_ENV === 'development') {
  const isBenignResizeObserverError = (msg) => {
    return typeof msg === 'string' && (
      msg.includes('ResizeObserver loop limit exceeded') ||
      msg.includes('ResizeObserver loop completed with undelivered notifications')
    )
  }

  // Capture phase listeners to stop overlay before it handles the event
  const stopIfResizeObserverError = (event) => {
    const msg = event?.message || (event?.reason && event.reason.message) || ''
    if (isBenignResizeObserverError(msg)) {
      event?.stopImmediatePropagation?.()
      event?.preventDefault?.()
      return false
    }
  }
  window.addEventListener('error', stopIfResizeObserverError, true)
  window.addEventListener('unhandledrejection', stopIfResizeObserverError, true)

  // Also filter console.error as an extra guard
  const origConsoleErrorEarly = console.error
  console.error = function (...args) {
    if (args && isBenignResizeObserverError(args[0])) {
      return
    }
    return origConsoleErrorEarly.apply(console, args)
  }
}

// A stale JS chunk (old hash, removed by a newer deploy/rebuild) sometimes gets
// a 200 response with the index.html SPA fallback body instead of a real 404.
// The <script> tag itself loads fine, so webpack's promise-based chunk loader
// never rejects with ChunkLoadError (router.onError below never sees it) — the
// browser just throws a raw "SyntaxError: Unexpected token '<'" while parsing
// the HTML as JS. Catch that specific pattern here and force the same reload
// router.onError does for genuine ChunkLoadErrors, sharing its throttle key.
window.addEventListener('error', (event) => {
  const isStaleChunkSyntaxError = /unexpected token/i.test(event?.message || '')
    && /\/js\/[\w-]+\.[0-9a-f]+\.js/i.test(event?.filename || '')
  if (!isStaleChunkSyntaxError) return

  const RELOAD_KEY = 'chunk_reload_at'
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0)
  const now = Date.now()
  if (now - last > 10000) {
    sessionStorage.setItem(RELOAD_KEY, String(now))
    window.location.reload()
  }
})

if (window.Telegram && window.Telegram.WebApp) {
  const tg = window.Telegram.WebApp;
  tg.ready();
  tg.expand();
  // Prevents swipe-down from closing/minimizing the mini app when the
  // user scrolls up inside it (requires Bot API 7.7+, older clients ignore it).
  if (typeof tg.disableVerticalSwipes === 'function') {
    tg.disableVerticalSwipes();
  }
  // The client decides "this drag is a close gesture" at touchstart, purely by
  // checking whether the page's scrollTop is already 0 — disableVerticalSwipes and
  // overscroll-behavior only affect what happens *during* the drag, so on a fast
  // flick (no pause) the client has already committed to closing before either
  // kicks in. Keeping scrollTop pinned at >=1 means that check never sees "at top",
  // so the close gesture never starts in the first place, regardless of swipe speed.
  const pinScrollAboveTop = () => {
    // On short pages (little content, empty states) there's nothing to scroll to —
    // scrollTo(0,1) would be a no-op and scrollY would stay stuck at 0. min-height
    // (not height, which would clip content that later grows past 100vh in this SPA)
    // guarantees 1px of headroom so the pin below always has somewhere to land.
    if (document.documentElement.scrollHeight <= window.innerHeight) {
      document.documentElement.style.setProperty('min-height', 'calc(100vh + 1px)', 'important');
    }
    if (window.scrollY <= 0) window.scrollTo(0, 1);
  };
  window.addEventListener('load', pinScrollAboveTop);
  window.addEventListener('scroll', pinScrollAboveTop, { passive: true });
  // touchstart fires at the exact moment the client checks scrollTop, before the
  // 'scroll' event would — closes the gap on route changes that shrink the page
  // (SPA navigation doesn't refire 'load') between navigating and the next touch.
  document.addEventListener('touchstart', pinScrollAboveTop, { passive: true });
  requestAnimationFrame(pinScrollAboveTop);
}

// Capture ?ref=CODE referral code into a shared cookie (domain=.graspil.com) so it
// survives the jump from the landing page (graspil.com) to this app (app.graspil.com).
{
  const refParam = new URLSearchParams(window.location.search).get('ref')
  if (refParam) {
    document.cookie = `graspil_ref=${encodeURIComponent(refParam)}; domain=.graspil.com; path=/; max-age=${365 * 24 * 3600}; SameSite=Lax`
  }
}

import { createApp } from "vue";
import App from "./App.vue";
import store from "./store/store";
import router from "./router";

import i18n from './i18n'
import ElementPlus from 'element-plus'
// import 'element-plus/dist/index.css'
// import Vuex from 'vuex'

import "./assets/css/nucleo-icons.css";
import "./assets/css/nucleo-svg.css";
import ArgonDashboard from "./argon-dashboard";
import 'element-plus/dist/index.css'
import { GraphQLClient } from 'graphql-request';
// import VueMatomo from 'vue-matomo'
import posthogPlugin from "./plugins/posthog"; //import the plugin.
import { VueQueryPlugin } from '@tanstack/vue-query'
import { queryClient } from './plugins/vueQuery'


const hygraphClient = new GraphQLClient(
    'https://api-eu-central-1-shared-euc1-02.hygraph.com/v2/cllm4iqkx1aa501t598gggh0e/master'
);


const appInstance = createApp(App);
appInstance.mixin({
    beforeCreate() {
        this.$hygraph = hygraphClient;
    },
})

// appInstance.use(Vuex)
appInstance.use(store);
appInstance.use(i18n)
appInstance.use(router);
appInstance.use(ArgonDashboard);
appInstance.use(ElementPlus)
appInstance.use(posthogPlugin);
appInstance.use(VueQueryPlugin, { queryClient })

// appInstance.use(VueMatomo, {
//     host: 'https://stat.graspil.com',
//     siteId: 1,
// })
appInstance.mount("#app");

import  "bootstrap/dist/js/bootstrap.js"


// Dev-only: suppress benign Chrome ResizeObserver loop error in overlay
if (process.env.NODE_ENV === 'development') {
  const isBenignResizeObserverError = (msg) => {
    return typeof msg === 'string' && (
        msg.includes('ResizeObserver loop limit exceeded') ||
        msg.includes('ResizeObserver loop completed with undelivered notifications')
    )
  }

  // Filter console.error so webpack overlay doesn't pop for this known benign warning
  const origConsoleError = console.error
  console.error = function (...args) {
    if (args && isBenignResizeObserverError(args[0])) {
      return
    }
    return origConsoleError.apply(console, args)
  }

  // Also stop propagation of the specific window error
  const stopIfResizeObserverError = (event) => {
    const msg = event?.message || (event?.reason && event.reason.message) || ''
    if (isBenignResizeObserverError(msg)) {
      event?.stopImmediatePropagation?.()
      event?.preventDefault?.()
      return false
    }
  }

  window.addEventListener('error', stopIfResizeObserverError)
  window.addEventListener('unhandledrejection', stopIfResizeObserverError)
}


// import bootstrap from 'bootstrap'
//
// var tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
// tooltipTriggerList.map(function (tooltipTriggerEl) {
//     return new bootstrap.Tooltip(tooltipTriggerEl)
// })
